import React, {
    CSSProperties,
    forwardRef,
    PropsWithChildren,
    RefObject,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
} from 'react';
import {useTranslation} from 'react-i18next';
import DropdownMenuCompactTile, {DropdownMenuCompactTileProps} from './DropdownMenuCompactTile';
import './dropdownMenuCompact.scss';

export enum DropdownMenuCompactMenuPosition {
    LEFT = 'c-dropdownMenuCompact__menu--left',
    RIGHT = 'c-dropdownMenuCompact__menu--right',
    UP = 'c-dropdownMenuCompact__menu--up',
    DOWN = 'c-dropdownMenuCompact__menu--down',
}

export interface DropdownMenuCompactRef {
    closeMenu: () => void;
}

export const DROPDOWN_MENU_COMPACT_SUBMENU_HOVER_OPEN_CLOSE_MS = 500;

interface DropdownMenuCompactProps {
    header?: JSX.Element;
    items: DropdownMenuCompactTileProps[][];
    menuRenderKey?: unknown;
    level?: number;
    className?: string;
    menuPosition?: DropdownMenuCompactMenuPosition;
    fixed?: 'top' | 'bottom' | 'left' | 'right';
    disabled?: boolean;
    closeParent?: () => void;
    openOnMouseEnter?: boolean;
    openOnMouseEnterDelayMs?: number;
    closeOnMouseExit?: boolean;
    closeOnMouseExitDelayMs?: number;
    onMenuClose?: () => void;
    onClickOutside?: () => void;
    onHoverEnter?: () => void;
    onHoverExit?: () => void;
    width?: 'fit' | 'full' | 'wide';
}

const DropdownMenuCompact = forwardRef<DropdownMenuCompactRef, PropsWithChildren<DropdownMenuCompactProps>>(
    (
        {
            header,
            items,
            level = 0,
            className,
            menuPosition,
            fixed,
            disabled,
            closeParent,
            children,
            openOnMouseEnter,
            openOnMouseEnterDelayMs,
            closeOnMouseExit,
            closeOnMouseExitDelayMs,
            onMenuClose,
            onClickOutside,
            onHoverEnter,
            onHoverExit,
            width = 'fit',
        },
        ref
    ) => {
        const {t} = useTranslation();

        const [dropdownOpen, setDropdownOpen] = React.useState(false);
        const menuRef = useRef<HTMLDivElement>(null);
        const wrapperRef = useRef<HTMLDivElement>(null);
        const buttonRef = useRef<HTMLDivElement>(null);
        const hoverOpenTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
        const hoverCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

        const resolvedMouseEnterDelayMs = useMemo((): number => {
            if (openOnMouseEnterDelayMs !== undefined) {
                return openOnMouseEnterDelayMs;
            }
            if (openOnMouseEnter && level > 0) {
                return DROPDOWN_MENU_COMPACT_SUBMENU_HOVER_OPEN_CLOSE_MS;
            }
            return 0;
        }, [openOnMouseEnterDelayMs, openOnMouseEnter, level]);

        const resolvedMouseExitDelayMs = useMemo((): number => {
            if (closeOnMouseExitDelayMs !== undefined) {
                return closeOnMouseExitDelayMs;
            }
            if (closeOnMouseExit && level > 0) {
                return DROPDOWN_MENU_COMPACT_SUBMENU_HOVER_OPEN_CLOSE_MS;
            }
            return 0;
        }, [closeOnMouseExitDelayMs, closeOnMouseExit, level]);

        const clearHoverOpenTimer = useCallback((): void => {
            if (hoverOpenTimerRef.current != null) {
                clearTimeout(hoverOpenTimerRef.current);
                hoverOpenTimerRef.current = null;
            }
        }, []);

        const clearHoverCloseTimer = useCallback((): void => {
            if (hoverCloseTimerRef.current != null) {
                clearTimeout(hoverCloseTimerRef.current);
                hoverCloseTimerRef.current = null;
            }
        }, []);

        useEffect(
            () => () => {
                clearHoverOpenTimer();
                clearHoverCloseTimer();
            },
            [clearHoverOpenTimer, clearHoverCloseTimer]
        );

        useImperativeHandle(
            ref,
            () => ({
                closeMenu: () => {
                    clearHoverOpenTimer();
                    clearHoverCloseTimer();
                    setDropdownOpen(false);
                },
            }),
            [clearHoverOpenTimer, clearHoverCloseTimer]
        );

        const handleSetDropdownOpen = useCallback(
            (isOpen: boolean): void => {
                setDropdownOpen(isOpen);
                if (!isOpen) closeParent?.();
            },
            [closeParent]
        );

        const handleMouseEnter = useCallback((): void => {
            clearHoverCloseTimer();
            if (openOnMouseEnter) {
                const delayMs = resolvedMouseEnterDelayMs;
                if (delayMs > 0) {
                    clearHoverOpenTimer();
                    hoverOpenTimerRef.current = setTimeout(() => {
                        hoverOpenTimerRef.current = null;
                        setDropdownOpen(true);
                    }, delayMs);
                } else {
                    setDropdownOpen(true);
                }
            }
            onHoverEnter?.();
        }, [openOnMouseEnter, resolvedMouseEnterDelayMs, onHoverEnter, clearHoverOpenTimer, clearHoverCloseTimer]);

        const handleMouseLeave = useCallback((): void => {
            clearHoverOpenTimer();
            if (!closeOnMouseExit) {
                onHoverExit?.();
                return;
            }
            const delayMs = resolvedMouseExitDelayMs;
            if (delayMs > 0) {
                clearHoverCloseTimer();
                hoverCloseTimerRef.current = setTimeout(() => {
                    hoverCloseTimerRef.current = null;
                    setDropdownOpen(false);
                }, delayMs);
            } else {
                setDropdownOpen(false);
            }
            onHoverExit?.();
        }, [closeOnMouseExit, resolvedMouseExitDelayMs, onHoverExit, clearHoverOpenTimer, clearHoverCloseTimer]);

        const handleButtonClick = useCallback((): void => {
            if (disabled) return;
            clearHoverOpenTimer();
            clearHoverCloseTimer();
            if (openOnMouseEnter) {
                // Delayed-hover submenus: click toggles without waiting. Top-level hover menus (delay 0)
                if (resolvedMouseEnterDelayMs > 0) {
                    setDropdownOpen((prev) => !prev);
                }
                return;
            }
            setDropdownOpen((prev) => !prev);
        }, [openOnMouseEnter, resolvedMouseEnterDelayMs, disabled, clearHoverOpenTimer, clearHoverCloseTimer]);

        const hasSubMenu = useMemo(() => {
            return items.some((group) => group.some((item) => 'items' in item && Array.isArray(item.items) && item.items.length > 0));
        }, [items]);

        const menuPositionStyles = useCallback(
            (targetRef: RefObject<HTMLDivElement>): CSSProperties => {
                const rect = targetRef.current?.getBoundingClientRect();

                if (!fixed || !rect) {
                    return {
                        '--level': level?.toString(),
                    } as React.CSSProperties;
                }

                const {innerWidth, innerHeight} = window;
                const {left, bottom, top, right} = rect;

                if (fixed === 'top') {
                    return {
                        '--level': level?.toString(),
                        position: 'fixed',
                        top: 'auto',
                        bottom: `${innerHeight - top}px`,
                        left: `${left}px`,
                    } as React.CSSProperties;
                }
                if (fixed === 'bottom') {
                    return {
                        '--level': level?.toString(),
                        position: 'fixed',
                        bottom: 'auto',
                        top: `${bottom}px`,
                        left: `${left}px`,
                        maxWidth: `${innerWidth - left - 12}px`,
                        maxHeight: `${innerHeight - bottom - 12}px`,
                    } as React.CSSProperties;
                }
                if (fixed === 'left') {
                    return {
                        '--level': level?.toString(),
                        position: 'fixed',
                        left: 'auto',
                        right: `${innerWidth - left}px`,
                        top: `${top}px`,
                    } as React.CSSProperties;
                }
                if (fixed === 'right') {
                    return {
                        '--level': level?.toString(),
                        position: 'fixed',
                        right: 'auto',
                        left: `${right}px`,
                        top: `${top}px`,
                    } as React.CSSProperties;
                }

                return {} as React.CSSProperties;
            },
            [fixed, level]
        );

        useEffect(() => {
            const applyMenuPositionClasses = (): void => {
                if (!menuRef.current || !wrapperRef.current) return;

                menuRef.current.classList.remove(
                    DropdownMenuCompactMenuPosition.LEFT,
                    DropdownMenuCompactMenuPosition.RIGHT,
                    DropdownMenuCompactMenuPosition.UP
                );

                if (menuPosition) {
                    menuRef.current.classList.add(menuPosition);
                    return;
                }

                const menu = menuRef.current.getBoundingClientRect();

                if (menu.right > window.innerWidth) {
                    menuRef.current.classList.add(DropdownMenuCompactMenuPosition.LEFT);
                } else if (menu.left < 0) {
                    menuRef.current.classList.add(DropdownMenuCompactMenuPosition.RIGHT);
                }

                if (menu.bottom > window.innerHeight) {
                    menuRef.current.classList.add(DropdownMenuCompactMenuPosition.UP);
                }
            };

            applyMenuPositionClasses();

            if (dropdownOpen) {
                window.addEventListener('resize', applyMenuPositionClasses);
            }

            return () => {
                window.removeEventListener('resize', applyMenuPositionClasses);
            };
        }, [menuPosition, dropdownOpen, items]);

        useEffect(() => {
            const listener = (event: Event | TouchEvent): void => {
                if (
                    menuRef.current &&
                    !menuRef.current.contains(event.target as Node) &&
                    !wrapperRef.current?.contains(event.target as Node)
                ) {
                    if (dropdownOpen) {
                        onClickOutside?.();
                        onMenuClose?.();
                    }
                    setDropdownOpen(false);
                }
            };

            document.addEventListener('mousedown', listener);
            document.addEventListener('touchstart', listener);
            window.addEventListener('scroll', listener);

            return () => {
                document.removeEventListener('mousedown', listener);
                document.removeEventListener('touchstart', listener);
                window.removeEventListener('scroll', listener);
            };
        }, [dropdownOpen, onMenuClose, onClickOutside]);

        const renderedItems = useMemo(() => {
            if (!items.length) return null;
            if (items[0].length === 0) {
                return (
                    <DropdownMenuCompactTile
                        key={1}
                        label={t('common.noOptions')}
                        disabled
                        level={level + 1}
                        setDropdownOpen={handleSetDropdownOpen}
                        onClick={() => {
                            // empty
                        }}
                    />
                );
            }

            return items.map((group, index) => (
                <div key={index} className="c-dropdownMenuCompact__menu_group">
                    {group.map((item, itemIndex) => {
                        const {getDisabled, disabled: itemDisabled, ...tileProps} = item;
                        const resolvedDisabled = getDisabled ? getDisabled() : itemDisabled;

                        return (
                            <DropdownMenuCompactTile
                                key={itemIndex}
                                {...tileProps}
                                disabled={resolvedDisabled}
                                level={level + 1}
                                setDropdownOpen={handleSetDropdownOpen}
                                position={menuPosition}
                                font={item.font}
                                dataCy={item.dataCy}
                            />
                        );
                    })}
                </div>
            ));
        }, [items, level, handleSetDropdownOpen, menuPosition, t]);

        if (!items.length) return children ? <>{children}</> : null;

        const isSubmenuTriggerWithDelayedHover = resolvedMouseEnterDelayMs > 0;
        const triggerActiveClass = dropdownOpen && isSubmenuTriggerWithDelayedHover ? 'c-dropdownMenuCompact--trigger-active' : '';

        return (
            <div
                ref={wrapperRef}
                className={`c-dropdownMenuCompact ${dropdownOpen ? 'c-dropdownMenuCompact--open' : ''} ${triggerActiveClass} ${
                    className || ''
                }`}
                data-cy="dropdownMenuCompact"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                <div ref={buttonRef} className="c-dropdownMenuCompact__button" onClick={handleButtonClick}>
                    {children}
                </div>
                <div
                    className={`c-dropdownMenuCompact__menu c-dropdownMenuCompact__menu--${width} ${
                        level > 0 ? 'c-dropdownMenuCompact__menu--sub' : ''
                    } ${menuPosition || ''}`}
                    style={{...menuPositionStyles(buttonRef), overflowY: hasSubMenu ? 'initial' : 'auto'}}
                    ref={menuRef}
                >
                    {header}
                    {renderedItems}
                </div>
            </div>
        );
    }
);

export default DropdownMenuCompact;
